import { ButtonDefault } from '@/components/common/Button'
import {
  FilterInputBracket,
  FilterInputBudget,
  FilterInputCardtype,
  FilterInputColorId,
  FilterInputPreconCommander,
  FilterInputTag,
  FilterInputTimeframe,
  FilterLabel,
} from '@/components/common/Filter'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { useIsNavigating } from '@/components/providers/NavigationProvider'
import { usePage } from '@/components/providers/PageProvider'
import { FILTER_ADVANCED_MAX } from '@/defs/filters'
import { useToggleFiltersPanel } from '@/hooks/useToggleFiltersPanel'
import { getClickawayIgnoreProps } from '@/utils/clickawayUtil'
import { cn } from '@/utils/commonUtil'
import { formatFullNumber } from '@/utils/formatUtil'

import styles from './FeaturedCommonFilters.module.scss'

const CONFIG = [
  {
    filter: 'tags',
    renderInput: () => <FilterInputTag variant='popular' />,
    renderLabel: () => <FilterLabel variant='tagPopular' />,
  },
  {
    filter: 'cardtypes',
    renderInput: () => <FilterInputCardtype />,
    renderLabel: () => <FilterLabel variant='cardtype' />,
  },
  {
    filter: 'preconCommanders',
    renderInput: () => <FilterInputPreconCommander size='sm' />,
    renderLabel: () => <FilterLabel variant='preconCommander' />,
  },
  {
    filter: 'colorIdMap',
    renderInput: () => <FilterInputColorId />,
    renderLabel: () => <FilterLabel variant='colorId' />,
  },
  {
    filter: 'timeframes',
    renderInput: () => <FilterInputTimeframe />,
    renderLabel: () => <FilterLabel variant='timeframe' />,
  },
  {
    filter: 'brackets',
    renderInput: () => <FilterInputBracket />,
    renderLabel: () => <FilterLabel variant='bracket' />,
  },
  {
    filter: 'budgets',
    renderInput: () => <FilterInputBudget />,
    renderLabel: () => <FilterLabel variant='budget' />,
  },
] as const

export const FeaturedCommonFilters = () => {
  const { page } = usePage()
  const isNavigating = useIsNavigating()

  const route = page?.route
  const hasAdvanced = !!page?.optionsFilters?.advanced
  const countAdvanced = route?.advancedFilters?.length || 0

  const toggleFiltersPanel = useToggleFiltersPanel(countAdvanced < FILTER_ADVANCED_MAX ? 'advanced' : undefined)

  return (
    <ul className={styles.container}>
      {CONFIG.map(({ filter, renderInput, renderLabel }) =>
        page?.optionsFilters?.[filter] ? (
          <li className={cn(styles.item, { filter, styles })} key={filter}>
            {renderLabel()}
            <div className={styles.input}>{renderInput()}</div>
          </li>
        ) : null,
      )}
      {hasAdvanced && (
        <li className={cn(styles.item, { filter: 'advanced', styles })}>
          <FilterLabel variant='advanced' />
          <div className={styles.input}>
            <ButtonDefault
              fontNormal
              maxwidth
              onClick={toggleFiltersPanel}
              showLoadingMask={isNavigating}
              variant='group'
              {...getClickawayIgnoreProps(['collapse-expanded'])}
            >
              <span className={styles.button}>
                {countAdvanced > 0 ? (
                  <IconWithLabel iconAfter='sliders' iconAfterSize='sm' width='full'>
                    {`${formatFullNumber(countAdvanced)} active filter${countAdvanced > 1 ? 's' : ''}`}
                  </IconWithLabel>
                ) : (
                  <IconWithLabel iconAfter='plus' iconAfterSize='sm' width='full'>
                    Add filter
                  </IconWithLabel>
                )}
              </span>
            </ButtonDefault>
          </div>
        </li>
      )}
    </ul>
  )
}
